import { useEffect, useState } from 'react'
import { FormProvider, useForm, type Resolver } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'

import { EditIcon } from '@assets/svgs/EditIcon'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { setInitialValues } from './helpers/setInitialValues'
import { ReturnModal } from '@components/modals/ReturnModal'
import { WarningIcon } from '@assets/svgs/WarningIcon'
import { BaseModal2 } from '@components/modals/BaseModal2'
import { editDetailedSummaryModalSchema } from './form/validations'
import { useConsumption } from '@pages/Consumption/context'
import { Input } from '@components/shared/Input'
import { Select } from '@components/shared/Select'
import { Textarea } from '@components/shared/Textarea'
import { RowBox } from '@components/shared/RowBox'
import { useUser } from '@hooks/useUser'

import type { EditDetailedSummaryFormProps } from './form/types'
import type { EditDetailedSummaryModalProps } from './types'

import * as S from './styles'

const CATEGORY_OPTIONS = [
  { value: 'consumo_cd', label: 'Consumo CD' },
  { value: 'consumo_pap', label: 'Consumo PAP' },
  { value: 'reembolso', label: 'Reembolso' },
  { value: 'gastos_cd', label: 'Gastos CD' },
  { value: 'logistica', label: 'Logística' },
  { value: 'mat_limpeza', label: 'Mat. Limpeza' },
  { value: 'cred_piscofins_cd', label: 'Cred. PIS/COFINS CD' },
  { value: 'cred_piscofins_pap', label: 'Cred. PIS/COFINS PAP' },
  { value: 'var_est_alim', label: 'Var. Est. Alim.' },
  { value: 'outros_ajustes', label: 'Outros Ajustes' },
]

export const EditDetailedSummary = ({
  data,
  rows: _rows,
  open,
  onClose,
  canEdit = true,
  ...rest
}: EditDetailedSummaryModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const methods = useForm<EditDetailedSummaryFormProps>({
    defaultValues: initialFormValues,
    resolver: editDetailedSummaryModalSchema as unknown as Resolver<EditDetailedSummaryFormProps>
  })

  const { filiData } = useUser()
  const { isEditDetailedSummaryLoading, editDetailedSummary, detailedSummaryV2Data } = useConsumption()

  const [openCancelModal, setOpenCancelModal] = useState(false)

  const onSubmit = (formData: EditDetailedSummaryFormProps) => {
    editDetailedSummary({
      branchCode: filiData?.filiCode || '',
      competenceDate: filiData?.startPeriod || '',
      weekNumber: detailedSummaryV2Data?.weekNumber ?? 0,
      categoryKey: formData.categoryKey,
      adjustmentValue: Number(
        (formData.totalValue.includes('-') ? '-' : '') +
        formData.totalValue.replace(/[^\d,]/g, '').replace(',', '.')
      ),
      observations: formData.observations
    }).then((success) => success && onClose(false))
  }

  useEffect(() => {
    if (data && open) {
      methods.reset(setInitialValues(data))
    }
  }, [data, open])

  useEffect(() => {
    if (!open) methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        width={600}
        open={open}
        heightContainer={isMobile ? '100%' : 'auto'}
        title="Editar Resumo Detalhado"
        headerIcon={<EditIcon />}
        onClose={() => setOpenCancelModal(true)}
        footer={{
          cancelButtonText: 'Cancelar',
          cancelButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          confirmButtonText: 'Confirmar',
          confirmButtonMinWidth: isMobile ? 'calc(50% - 8px)' : 193.5,
          onConfirm: () => methods.handleSubmit(onSubmit)(),
          loading: isEditDetailedSummaryLoading
        }}
        {...rest}
      >
        <S.Title>Preencha todos os campos antes de confirmar a edição do resumo.</S.Title>

        <FormProvider {...methods}>
          <S.Form>
            <S.FieldsRow>
              <Select
                name="categoryKey"
                label="Categoria"
                required
                options={CATEGORY_OPTIONS}
                disabled
              />

              <Input
                name="totalValue"
                label="Valor Total"
                required
                maskType="currency"
              />
            </S.FieldsRow>

            <RowBox>
              <Textarea
                name="observations"
                label="Observações"
                placeholder="Descreva as suas observações..."
                required
              />
            </RowBox>
          </S.Form>
        </FormProvider>
      </BaseModal2>

      <ReturnModal
        open={openCancelModal}
        headerIcon={<WarningIcon />}
        title="Deseja realmente cancelar a ação?"
        message="Todo o seu progresso será perdido!"
        headerTitle="Você tem certeza?"
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenCancelModal}
        onConfirm={() => {
          onClose(false)
          setOpenCancelModal(false)
        }}
      />
    </>
  )
}
